import React from "react";
import { useDispatch } from "react-redux";
import moment from "moment";
import { updateShopOrder } from "../../actions/shop";
import { ImgIcon } from "../../assets";
import ClockIcon from "./ClockIcon";
import { useModal } from "../../assets/useModal";
import EtaSelector from "../EtaSelector";
import { COLORS, DELIVERY } from "../../constants";
import OrderStatusChanger from "./OrderStatusChanger";
import { vendorPath, groupArrayByField } from "../../helpers";

function TimeSince({ eta }) {
	let overdue = moment(eta).diff(moment()) <= 0;
	let color = overdue ? "#ce003b" : "#26c164";
	return (
		<div className="timeSince">
			<ClockIcon color={color} size={18} />
			<span style={{ color }}>
				{moment(eta).fromNow(true)} {overdue ? "over" : "left"}
			</span>
		</div>
	)
}

export default function OrderTile({ order, editable = false, renderVendor = false }) {

	const dispatch = useDispatch();
	const { openModal, setModal, closeModal } = useModal();

	let addressLine;
	if (order.deliveryAddress) addressLine = [
		order.deliveryAddress.line1,
		order.deliveryAddress.line2,
		order.deliveryAddress.town,
		order.deliveryAddress.postCode
	].filter(l => l).join(", ");

	let vendorAddressLine;
	if (order.vendor && order.vendor.address) vendorAddressLine = [
		order.vendor.address.line1,
		order.vendor.address.line2,
		order.vendor.address.town,
		order.vendor.address.postCode
	].filter(l => l).join(", ");

	const orderTotal = order.items.reduce((acc, item) => acc + item.price * item.quantity, 0);

	const orderFinished = ["completed", "rejected"].includes(order.status);

	const { featureColor1, featureColor2, logo, slug } = order.vendor;

	return <div
		key={order.id}
		className={`orderTile status-${order.status}-border-color`}
		style={{
			...(renderVendor && featureColor1 ? { borderColor: featureColor1 } : {}),
		}}
	>
		{renderVendor && <a
			href={vendorPath(slug)}
			target="_blank"
			className="padded vendor-section"
			rel="noopener noreferrer"
			style={{
				...(featureColor1 ? { backgroundColor: featureColor1 } : {}),
				...(featureColor2 ? { color: featureColor2 } : {})
			}}
		>
			<h4>{order.vendor.name}</h4>
			{logo && <ImgIcon
				bg="#ffffff"
				size={40}
				src={logo.url}
				cover={true}
			/>}
		</a>}
		<div className="inner" >
			<h4 className="flexy padded bordered" style={{ alignItems: "center" }}>
				<span title={order.id}><span className="brand-c">#{order.id}</span> {order.type}</span>
			</h4>
			<div className="eta featureFont padded">
				<span>Made - {moment(order.created_at).format("DD MMM HH:mm")}</span>
			</div>
			{Boolean(order.chosenTime) &&
				<div className="eta featureFont padded">
					<span>For - {moment(order.chosenTime).format("DD MMM HH:mm")}</span>
					{(!orderFinished && !order.eta) && <TimeSince eta={order.chosenTime} />}
				</div>
			}
			{Boolean(!renderVendor && order.eta && !orderFinished) &&
				<div className="eta featureFont padded">
					<span>ETA - {moment(order.eta).format("DD MMM HH:mm")}</span>
					<TimeSince eta={order.eta} />
				</div>
			}
			<div className="flexy wrapping">
				<div className="items bordered bordered-right">
					{order.items.map(item => {

						const optionTypes = groupArrayByField(item.options || [], "name");

						return <div className="flexy" key={item.id}>
							<span className="padded" style={{ flex: 4 }}>
								<div className="featureFont">
									{item.name}
								</div>
								{Object.entries(optionTypes).map(([name, options]) => (
									<div className="line-item-addOn-type" key={name}>
										<div>{name}:</div>
										{/* And then iterate each of the options */}
										{options.map(({ id: optionId, addOn, quantity }) => (
											<div className="line-item-addOn normalFont" key={optionId}>
												{addOn}{quantity !== 1 && ` x${quantity}`}
											</div>
										))}
									</div>
								))}
							</span>
							<span className="padded featureFont" style={{ flex: 1 }}>x {item.quantity}</span>
							<span className="padded text-right featureFont" style={{ flex: 1 }}>{`£${((item.price * item.quantity) / 100).toFixed(2)}`}</span>
						</div>
					})}
					<div className="flexy featureFont">
						<b className="padded" style={{ flex: 4 }}>Total</b>
						<b className="padded" style={{ flex: 1 }}></b>
						<b className="padded text-right" style={{ flex: 1 }}>{`£${(orderTotal / 100).toFixed(2)}`}</b>
					</div>
				</div>
				<div className="bordered">
					{/* User info */}
					{order.user.id && <>
						<div className="padded">{order.user.firstName} {order.user.lastName}</div>
						<a className="padded" href={`mailto:${order.user.email}`} >{order.user.email}</a>
						{order.user.tel && <a className="padded" href={`tel:${order.user.tel}`} >{order.user.tel}</a>}
					</>}
					{/* Address */}
					{Boolean(order.deliveryAddress && order.type === DELIVERY) && <>
						{order.deliveryAddress.mapLink ?
							(
								<a
									target="_blank"
									rel="noopener noreferrer"
									className="padded"
									href={order.deliveryAddress.mapLink}
								>
									<div style={{ marginBottom: 5 }}>Deliver to:</div>
									{addressLine}
								</a>
							) : (
								<div className="padded">
									<div style={{ marginBottom: 5 }}>Deliver to:</div>
									{addressLine}
								</div>
							)
						}
					</>}
					{(order.type === "collection" && vendorAddressLine) && <>
						{order.vendor.address.mapLink ?
							(
								<a
									target="_blank"
									rel="noopener noreferrer"
									className="padded"
									href={order.vendor.address.mapLink}
								>
									<div style={{ marginBottom: 5 }}>Collect from:</div>
									{vendorAddressLine}
								</a>
							) : (
								<div className="padded">
									<div style={{ marginBottom: 5 }}>Collect from:</div>
									{vendorAddressLine}
								</div>
							)
						}
					</>}
				</div>
			</div>
			{order.notes && <div className="notes bordered" style={{ whiteSpace: "pre-wrap", padding: "20px 15px" }}>
				<b>Notes:</b> {order.notes}
			</div>}

			{/* History of the item */}
			{Boolean(order.history && order.history.length) && <div>
				<OrderHistory history={order.history} currentStatus={order.status} />
			</div>}

			{false && <div className={`featureFont upperCase text-center status flexy`}>
				<span className="padded" style={{ flex: 3 }}>
					{order.status === "paid" ? "paid - awaiting confirmation" : order.status}
				</span>
			</div>}

			{/* For confirming an order */}
			{(false && editable && ["paid", "confirmed"].includes(order.status)) && <div
				className="flexy padded options bordered-top"
			>

				{/* Rejecting and refunding */}
				<button
					style={{ marginRight: 5 }}
					onClick={e => {
						if (!window.confirm("Are you sure you want to REJECT this order? We will notify your customer."))
							return;
						dispatch(updateShopOrder(order.id, { status: "rejected" }));
					}}
				>REJECT</button>

				{/* Completing order */}
				{order.status === "confirmed" && <button
					className="brand-bg"
					style={{ marginLeft: 5 }}
					onClick={e => {
						dispatch(updateShopOrder(order.id, { status: "completed" }));
					}}
				>
					COMPLETE
				</button>}

				{/* For confirming an order and setting the ETA */}
				{order.status === "paid" && <button
					className="brand-bg"
					style={{ marginLeft: 5 }}
					onClick={e => {
						setModal(
							<section key={order.id} className="time-section">
								<h2>{(() => {
									switch (order.type) {
										case "collection":
											return "When can they collect?";
										case "delivery":
											return "When will you deliver?";
										default:
											return "When will it be ready?"
									}
								})()}</h2>
								<br />
								<div className="inner">
									<EtaSelector
										orderId={order.id}
										onChange={date => {
											// if (!window.confirm("Are you sure you want to CONFIRM this order? We will notify your customer."))
											// 	return;
											dispatch(updateShopOrder(order.id, { status: "confirmed", eta: date }));
											closeModal();
											setModal(null);
										}}
									/>
								</div>
							</section>
						);
						openModal();
					}}
				>CONFIRM</button>}
			</div>}

			{/* Changing order status */}
			{editable && <div>
				<OrderStatusChanger order={order} />
			</div>}
		</div>
	</div>
}

function OrderHistory({ history, currentStatus }) {
	return <div className="orderHistory">

		{history.map(entry => {

			const entryStatus = entry.changes.status ? entry.changes.status : (currentStatus || "pending");
			const statusRgb = COLORS[entryStatus] || [0, 0, 0];

			return <div className="entry" key={entry.id || entry.timestamp}>
				<div className={`bar-before status-${entryStatus}-bg-color`} style={{
					background: `linear-gradient(0deg, rgb(${statusRgb.join(",")}) 50%, rgba(${statusRgb.join(",")}, 0) 100%)`
				}} />
				<div className={`bar-after status-${entryStatus}-bg-color`} />
				<div className={`icon status-${entryStatus}-bg-color status-${entryStatus}-border-color`} />
				<div className="time">
					{moment(entry.timestamp).format("HH:mm [on] DD MMM YYYY")}
				</div>
				{entry.comment && <div className="comment">
					{entry.comment}
				</div>}
				{Boolean(Object.keys(entry.changes).length) && <div className="details">
					{Object.entries(entry.changes).map(([key, value]) => (
						<div className="detail-item" key={key}>{detailRenderer(key, value)}</div>
					))}
				</div>}
			</div>

		})}

	</div>
}

function detailRenderer(key, value) {
	switch (key) {
		case "eta":
			return `Order now due ${moment(value).format("HH:mm")}`;
		case "status":
			return `Status is now ${value}.`
		default:
			return `${key} changed to ${value}.`
	}
}